import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { cn } from "@/utils/utils";
import { useNewFieldsUI } from "@/services/features/useNewFieldsUI";
import Modal from "@/components/controls/Modal";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import Input from "@/components/ui/input";
import IconContainer from "@/components/ui/icon-container";
import { CancelSchedule, InfoCircle } from "@dreamhub/designsystem/icons";
import { SingleSelect } from "@/components/controls/SingleSelect";
import { MultiSelect } from "@/components/controls/MultiSelect";
import DateTimeField from "@/components/controls/DateTimeField";
import { useFiscalYearStartMonth } from "@/entities/settings/useFiscalYearStartMonth";
import { isoToTimeString, localTimeStringToIso } from "@/utils/datetime";
import {
  useCreateReportingScheduler,
  useDeleteReportingScheduler,
  useUpdateReportingScheduler
} from "@/entities/reportingScheduler/hooks";
import {
  DeliveryChannel,
  ReportingScheduler
} from "@/entities/reportingScheduler/types";
import {
  alreadySavedReportId,
  useUnscheduleReport
} from "@/entities/savedReports/hooks";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { useScheduleConfirmation } from "@/components/global/Chat/components/dreamerReports/useScheduleConfirmation";
import { ScheduledReportToast } from "@/components/global/Chat/components/dreamerReports/ScheduledReportToast";
import { useChatTabs } from "@/components/global/Chat/components/chat/ChatTabsContext";
import { useChat } from "@/components/layouts/Chat/ChatContext";
import { buildScheduleSummary } from "@/components/global/Chat/components/ScheduleModal/buildScheduleSummary";
import { usePeriodFieldByFrequency } from "@/components/global/Chat/components/ScheduleModal/usePeriodFieldByFrequency";
import RemoveScheduleConfirmationModal from "@/components/global/Chat/components/ScheduleModal/RemoveScheduleConfirmationModal";
import {
  DayOfWeek,
  Frequency,
  ScheduleData
} from "@/components/global/Chat/components/ScheduleModal/types";

const TRANSLATION_KEY = "chat.scheduleModal";

const SHORT_MONTH_HINT_DAYS = new Set(["29", "30", "31"]);

const DEFAULT_COUNT = 1;
const DEFAULT_UNIT = Frequency.WEEK;
const DEFAULT_DAY_OF_PERIOD: string[] = [DayOfWeek.MONDAY];
const DEFAULT_DELIVERY_TIME = "8:00 AM";
const DEFAULT_CHANNELS: DeliveryChannel[] = ["CHAT"];

interface ScheduleModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onSchedule?: (data: ScheduleData) => void;
  onCreated?: (created: ReportingScheduler) => void;
  onSaved?: (updated: ReportingScheduler) => void;
  onCanceled?: () => void;
  messageId: string;
  reportName?: string;
  /**
   * The report row this answer already has, if the user saved it. Saving and
   * scheduling write the same `reports_schedules` row, so a saved report has an
   * id before it has a cadence, and before any caller has loaded that row.
   */
  savedReportId?: string | null;
  /** The report's row, with or without a cadence; only a cadence makes it an edit. */
  existingScheduleData?: ReportingScheduler | null;
  /** True while the report's row is still loading. */
  isReportRowLoading?: boolean;
  /** True once the report's row failed to load; the modal says so and closes. */
  isReportRowFailed?: boolean;
}

const ScheduleModal: React.FC<ScheduleModalProps> = ({
  isOpen,
  setIsOpen,
  onSchedule,
  onCreated,
  onSaved,
  onCanceled,
  messageId,
  reportName,
  savedReportId = null,
  existingScheduleData,
  isReportRowLoading = false,
  isReportRowFailed = false
}) => {
  const { t } = useTranslation();
  const isNewFieldsUI = useNewFieldsUI();
  const appendConfirmation = useScheduleConfirmation();

  const createScheduler = useCreateReportingScheduler();
  const updateScheduler = useUpdateReportingScheduler();
  const deleteScheduler = useDeleteReportingScheduler();
  const unscheduleReport = useUnscheduleReport();
  const { enabled: isMyReportsEnabled } = useFeatureFlag(
    FeatureFlags.CRM_10285_MY_REPORTS
  );
  const { showTab } = useChatTabs();
  const { forgetSavedReport } = useChat();

  const notifyScheduled = (
    kind: "created" | "updated",
    payload: ScheduleData
  ) => {
    if (!isMyReportsEnabled) return;
    const toastKey = kind === "created" ? "toastSuccess" : "toastUpdateSuccess";
    const message = t(
      `${TRANSLATION_KEY}.${toastKey}`,
      {
        description: buildScheduleSummary(t, payload, "delivery"),
        time: isoToTimeString(payload.deliveryTime)
      }
    );
    toast.success((shown) => (
      <ScheduledReportToast
        message={message}
        onManageReports={() => showTab("reports")}
        onDismiss={() => toast.dismiss(shown.id)}
      />
    ));
  };

  const [count, setCount] = useState<number>(DEFAULT_COUNT);
  const [unit, setUnit] = useState<Frequency>(DEFAULT_UNIT);
  const [dayOfPeriod, setDayOfPeriod] = useState<string | string[] | null>(
    DEFAULT_DAY_OF_PERIOD
  );
  const [deliveryTime, setDeliveryTime] = useState<string>(
    DEFAULT_DELIVERY_TIME
  );
  const [isTimeOpen, setIsTimeOpen] = useState(false);
  const [isRemoveConfirmOpen, setIsRemoveConfirmOpen] = useState(false);

  // A saved report's row has no cadence yet, so setting one is a first
  // schedule, not an edit.
  const existingConfig = existingScheduleData?.scheduleConfig ?? null;
  const isEditMode = !!existingConfig;

  // The fields take the cadence once per opening, when it is first in hand: the
  // row can arrive after the modal opens (the fields are held until then), and
  // a later refetch must not undo the user's edits.
  const hasFilledFromCadence = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      hasFilledFromCadence.current = false;
      setCount(DEFAULT_COUNT);
      setUnit(DEFAULT_UNIT);
      setDayOfPeriod(DEFAULT_DAY_OF_PERIOD);
      setDeliveryTime(DEFAULT_DELIVERY_TIME);
      setIsTimeOpen(false);
      return;
    }
    if (!existingConfig || hasFilledFromCadence.current) return;
    hasFilledFromCadence.current = true;
    setCount(existingConfig.frequencyCount);
    setUnit(existingConfig.frequencyUnit);
    setDayOfPeriod(existingConfig.dayOfPeriod);
    setDeliveryTime(isoToTimeString(existingConfig.deliveryTime));
  }, [isOpen, existingConfig]);

  // Without the report's row a schedule would overwrite its name, so a failed
  // lookup is said aloud and hands the user back, as the Reports tab does.
  useEffect(() => {
    if (!isOpen || !isReportRowFailed) return;
    toast.error(t("chat.reports.schedule.loadFailed"));
    setIsOpen(false);
  }, [isOpen, isReportRowFailed, setIsOpen, t]);

  const fiscalStartMonth = useFiscalYearStartMonth();

  const frequencyOptions = Object.values(Frequency).map((value) => ({
    id: value,
    displayName: t(`${TRANSLATION_KEY}.units.${value}`)
  }));

  const periodFieldByFrequency = usePeriodFieldByFrequency(t, fiscalStartMonth);
  const periodField = periodFieldByFrequency[unit];

  const handleFrequencyChange = (newUnit: Frequency) => {
    setUnit(newUnit);
    const nextDefault = periodFieldByFrequency[newUnit]?.defaultValue ?? null;
    setDayOfPeriod(
      newUnit === Frequency.WEEK && nextDefault !== null
        ? [nextDefault]
        : nextDefault
    );
    if (newUnit === Frequency.DAY) setCount(1);
  };

  const isPeriodFieldEmpty =
    unit !== Frequency.DAY &&
    (dayOfPeriod === null ||
      dayOfPeriod === "" ||
      (Array.isArray(dayOfPeriod) && dayOfPeriod.length === 0));

  const handleSchedule = () => {
    if (isPeriodFieldEmpty) return;
    const payload: ScheduleData = {
      frequencyCount: count,
      frequencyUnit: unit,
      dayOfPeriod,
      deliveryTime: localTimeStringToIso(deliveryTime),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
    onSchedule?.(payload);

    // Whether the report row exists decides the verb; whether it already had a
    // cadence decides what the user is told. A saved report has a row and no
    // cadence, so it takes the update path on its first schedule — a create
    // would be refused as a second save of the same answer.
    const reportId = existingScheduleData?.id ?? savedReportId;
    const name = existingScheduleData?.name ?? reportName;

    if (reportId) {
      updateScheduler.mutate(
        {
          pathParams: { id: reportId },
          data: {
            messageId: existingScheduleData?.messageId ?? messageId,
            ...(name ? { name } : {}),
            channels: DEFAULT_CHANNELS,
            scheduleConfig: payload
          }
        },
        {
          onSuccess: (saved) => {
            if (isEditMode) {
              appendConfirmation({ kind: "updated", payload });
              notifyScheduled("updated", payload);
              onSaved?.(saved);
            } else {
              appendConfirmation({ kind: "created", payload });
              notifyScheduled("created", payload);
              onCreated?.(saved);
            }
          },
          // The modal has already closed, so a failure has to be said aloud or
          // the cadence the user just set is lost without a trace.
          onError: () => {
            if (isMyReportsEnabled) toast.error(t("chat.reports.save.failed"));
          }
        }
      );
    } else {
      const onFirstSchedule = (created: ReportingScheduler) => {
        appendConfirmation({ kind: "created", payload });
        notifyScheduled("created", payload);
        onCreated?.(created);
      };
      const data = {
        messageId,
        ...(reportName ? { name: reportName } : {}),
        channels: DEFAULT_CHANNELS,
        scheduleConfig: payload
      };
      createScheduler.mutate(
        { data },
        {
          onSuccess: onFirstSchedule,
          onError: (error) => {
            if (!isMyReportsEnabled) return;
            // The answer was saved from somewhere this card could not see.
            // The refusal names that report, so the cadence goes onto it.
            const existingId = alreadySavedReportId(error);
            if (!existingId) {
              toast.error(t("chat.reports.save.failed"));
              return;
            }
            updateScheduler.mutate(
              { pathParams: { id: existingId }, data },
              {
                onSuccess: onFirstSchedule,
                onError: () => toast.error(t("chat.reports.save.failed"))
              }
            );
          }
        }
      );
    }
    setIsOpen(false);
  };

  const handleCancelSchedule = () => {
    if (!existingScheduleData?.id) return;
    const onSuccess = () => {
      onCanceled?.();
      setIsOpen(false);
    };
    // With saved reports, the schedule and the report are one row: deleting
    // it would take the report out of My Reports along with its cadence.
    if (isMyReportsEnabled) {
      unscheduleReport.mutate(
        {
          pathParams: { id: existingScheduleData.id },
          data: { scheduleConfig: null }
        },
        {
          onSuccess,
          onError: () =>
            toast.error(t("chat.reports.removeSchedule.failed"))
        }
      );
      return;
    }
    const deletedId = existingScheduleData.id;
    deleteScheduler.mutate(
      { pathParams: { id: deletedId } },
      {
        onSuccess: () => {
          // The row is gone, so no message may keep pointing a save at it.
          forgetSavedReport(deletedId);
          onSuccess();
        }
      }
    );
  };

  return (
    <>
      <Modal
      open={isOpen}
      setIsOpen={setIsOpen}
      title={
        <Text variant="lgSubtitle">
          {t(`${TRANSLATION_KEY}.${isEditMode ? "editTitle" : "title"}`)}
        </Text>
      }
      content={
        <div className="flex flex-col gap-4 p-6">
          <div className="flex items-center justify-between gap-4">
            <Text variant="bodySecondary">
              {t(`${TRANSLATION_KEY}.generateEvery`)}
            </Text>
            <div className="flex items-center gap-2">
              {unit !== Frequency.DAY && (
                <Input
                  type="number"
                  min={1}
                  value={count}
                  onChange={(e) =>
                    setCount(Math.max(1, Number(e.target.value) || 1))
                  }
                  containerClassName="w-[54px]"
                  className="text-center"
                  aria-label={t(`${TRANSLATION_KEY}.frequencyCountAriaLabel`)}
                  disabled={isReportRowLoading}
                />
              )}
              <div
                className={unit === Frequency.DAY ? "w-[220px]" : "w-[159px]"}
              >
                <SingleSelect
                  fieldName="schedule-frequency-unit"
                  options={frequencyOptions}
                  selectedValue={unit}
                  onSelection={(v) => handleFrequencyChange(v as Frequency)}
                  variant="form"
                  popoverClassName="w-[140px]"
                  disabled={isReportRowLoading}
                />
              </div>
            </div>
          </div>
          {periodField && (
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between gap-4">
                <Text variant="bodySecondary">
                  {t(`${TRANSLATION_KEY}.${periodField.labelKey}`)}
                </Text>
                <div className="w-[220px]">
                  {unit === Frequency.WEEK ? (
                    <MultiSelect
                      options={periodField.options.map((option) => ({
                        value: option.id,
                        label: option.displayName
                      }))}
                      selectedValues={
                        Array.isArray(dayOfPeriod) ? dayOfPeriod : []
                      }
                      onValueChange={setDayOfPeriod}
                      // Under My Reports the field is boxed like the frequency
                      // select beside it, and one day reads as text, not a chip.
                      variant={isMyReportsEnabled ? "form" : "inline"}
                      singleValueAsText={isMyReportsEnabled}
                      matchTriggerWidth
                      alwaysShowChevron
                      // The boxed border/hover belong to the pre-redesign
                      // trigger; under the flag `inline` paints a transparent
                      // border and a soft hover, which these would override.
                      className={cn(
                        "pr-2 transition-all duration-200",
                        // The form trigger is a block box; centre it like the
                        // selects beside it.
                        isMyReportsEnabled && "flex items-center",
                        !isNewFieldsUI &&
                          !isMyReportsEnabled &&
                          "border-border-field-default hover:!bg-surface-field-default hover:border-border-field-hover"
                      )}
                      placeholder={t(`${TRANSLATION_KEY}.selectDays`)}
                      disabled={isReportRowLoading}
                    />
                  ) : (
                    <SingleSelect
                      fieldName={`schedule-${periodField.labelKey}`}
                      options={periodField.options}
                      fixedOptions={periodField.fixedOptions}
                      selectedValue={
                        typeof dayOfPeriod === "string" ? dayOfPeriod : ""
                      }
                      onSelection={(v) => setDayOfPeriod(String(v))}
                      variant="form"
                      popoverClassName="w-[220px]"
                      showSelectedItemSubtitle={false}
                      disabled={isReportRowLoading}
                    />
                  )}
                </div>
              </div>
              {unit === Frequency.MONTH &&
                typeof dayOfPeriod === "string" &&
                SHORT_MONTH_HINT_DAYS.has(dayOfPeriod) && (
                  <div
                    className="self-end w-[220px] flex items-start gap-1.5"
                    data-test="schedule-short-month-hint"
                  >
                    <IconContainer
                      size="sm"
                      color="secondary"
                      className="mt-0.5 flex-shrink-0"
                    >
                      <InfoCircle />
                    </IconContainer>
                    <Text variant="smBodySecondary">
                      {t(`${TRANSLATION_KEY}.shortMonthHint`)}
                    </Text>
                  </div>
                )}
            </div>
          )}
          <div className="flex items-center justify-between gap-4">
            <Text variant="bodySecondary">
              {t(`${TRANSLATION_KEY}.deliveryTime`)}
            </Text>
            <div className="w-[220px]">
              <DateTimeField
                type="time"
                name="schedule-delivery-time"
                value={deliveryTime}
                onChange={setDeliveryTime}
                isOpen={isTimeOpen}
                onOpenChange={setIsTimeOpen}
                nearestTime={deliveryTime}
                disabled={isReportRowLoading}
              />
            </div>
          </div>
        </div>
      }
      footer={
        <div className="flex items-center justify-between gap-3">
          {isEditMode ? (
            <Button
              variant="tertiary"
              destructive
              onClick={() => setIsRemoveConfirmOpen(true)}
              disabled={
                deleteScheduler.isPending || unscheduleReport.isPending
              }
              data-test="cancel-schedule-button"
            >
              <IconContainer size="sm" color="current">
                <CancelSchedule />
              </IconContainer>
              {t(`${TRANSLATION_KEY}.cancelScheduleAction`)}
            </Button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-3">
            <Button variant="secondaryNoBg" onClick={() => setIsOpen(false)}>
              {t("cancel")}
            </Button>
            <Button
              onClick={handleSchedule}
              // Confirming before the row loads would write the answer's own
              // name and message over the report's.
              disabled={isPeriodFieldEmpty || isReportRowLoading}
              data-test="confirm-schedule-button"
            >
              {t(
                `${TRANSLATION_KEY}.${
                  isEditMode
                    ? "save"
                    : isMyReportsEnabled
                      ? "saveAndSchedule"
                      : "scheduleReport"
                }`
              )}
            </Button>
          </div>
        </div>
      }
      contentClassname="w-[560px]"
      hideDivider={true}
      data-test="schedule-modal"
      onOpenAutoFocus={(e) => e.preventDefault()}
    />
      <RemoveScheduleConfirmationModal
        isOpen={isRemoveConfirmOpen}
        setIsOpen={setIsRemoveConfirmOpen}
        onConfirm={handleCancelSchedule}
      />
    </>
  );
};

export default ScheduleModal;
